import {
  DocumentDuplicateIcon,
  TruckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';

import type { ReadVehicleWithOccupants } from '@/services/api/subjectsService';
import { enhancedApi } from '@/services/api/subjectsService';

import { createEntityLink } from '../core/createEntityLink';
import type { EntityTooltipContentProps } from '../core/types';
import {
  EmptyState,
  ErrorState,
  InfoRow,
  LoadingState,
  TooltipHeader,
  TooltipSection,
} from '../primitives';

/**
 * Vehicle entity type (re-export from subjectsService)
 */
export type Vehicle = ReadVehicleWithOccupants;

/**
 * Vehicle tooltip content component
 * Pure presentation component - memoized for performance
 */
export const VehicleTooltipContent = memo<EntityTooltipContentProps<Vehicle>>(
  ({ fetchState, isPersistent }) => {
    // Compute derived values before early returns
    const vehicle = fetchState.status === 'success' ? fetchState.data : null;

    const displayName = useMemo(() => {
      if (!vehicle) return null;
      return (
        [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') ||
        vehicle.label_in_police_report ||
        'Vehicle'
      );
    }, [vehicle]);

    const driver = useMemo(() => {
      if (!vehicle?.occupants) return null;
      return vehicle.occupants.find((occ) => occ.role === 'driver');
    }, [vehicle?.occupants]);

    const driverName = useMemo(() => {
      if (!driver?.person) return null;
      return (
        [driver.person.first_name, driver.person.last_name]
          .filter(Boolean)
          .join(' ') ||
        driver.person.short_description ||
        'Unnamed driver'
      );
    }, [driver]);

    // Handle loading state
    if (fetchState.status === 'pending') {
      return <LoadingState rows={2} />;
    }

    // Handle error state
    if (fetchState.status === 'error') {
      return <ErrorState error={fetchState.error} entityType="vehicle" />;
    }

    // Handle idle or empty state
    if (fetchState.status === 'idle' || !vehicle) {
      return <EmptyState entityType="vehicle" />;
    }

    // Success state
    const {
      license_plate,
      license_plate_state_code,
      vehicle_type,
      label_in_police_report,
    } = vehicle;

    const licensePlate = license_plate && (
      <>
        {license_plate}
        {license_plate_state_code && ` (${license_plate_state_code})`}
      </>
    );

    return (
      <div className="divide-y divide-gray-100">
        <TooltipHeader icon={TruckIcon} title={displayName || 'Vehicle'} />

        <TooltipSection withBorder>
          {driverName && (
            <InfoRow
              icon={UserIcon}
              label="Driver"
              value={
                <span className="font-medium text-blue-600">{driverName}</span>
              }
            />
          )}
          <InfoRow
            icon={DocumentDuplicateIcon}
            label="Plate"
            value={licensePlate}
          />
          <InfoRow
            icon={DocumentDuplicateIcon}
            label="VIN"
            value={vehicle.vin}
          />
        </TooltipSection>

        {vehicle.involvement_description && (
          <TooltipSection withBorder>
            <p className="text-sm text-gray-600">
              {vehicle.involvement_description}
            </p>
          </TooltipSection>
        )}
      </div>
    );
  },
);

VehicleTooltipContent.displayName = 'VehicleTooltipContent';

/**
 * Hoverable vehicle link with tooltip preview
 *
 * Shows vehicle identifier, driver (prominently), license plate, and type on hover.
 * Driver is highlighted in blue as it's critical for incident reconstruction.
 * Click to navigate to vehicle detail page.
 *
 * @example
 * ```tsx
 * <VehicleLink entityId={vehicleId} matterId={matterId}>
 *   2020 Toyota Camry
 * </VehicleLink>
 * ```
 */
export const VehicleLink = createEntityLink<
  Vehicle,
  { id: string; matterId: string }
>({
  tooltipContent: VehicleTooltipContent,
  useQuery: enhancedApi.endpoints.vehiclesWithOccupants.useQuery,
  getQueryArg: (id) => {
    // Note: matterId needs to be passed as part of entityId in format "matterId:vehicleId"
    const [matterId, vehicleId] = id.split(':');
    return { id: vehicleId || id, matterId: matterId || id };
  },
  displayName: 'VehicleLink',
});
